// Side-effects for public menu: OG tags, cart restore, deep-link, tracking, pixel.
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { findProductBySlug, findComboBySlug } from "@/utils/productSlug";
import { usePurchaseTracking } from "@/hooks/usePurchaseTracking";
import { initTracking, trackViewMenu, trackTableQrOpen, track } from "@/utils/tracking";
import { initBehavioralCommerce } from "@/utils/behavioralCommerce";

export function usePublicMenuEffects({
    restaurant, restaurantId, theme, BACKEND_BASE,
    checkout, cartToken, deepLinkSlug, loading, products, combos,
    pixelReady, cartTotal, isTableOrder, tableNumber,
    API, navigate, setCart, searchParams,
    setShowReservation, setReservationConfirmed,
}) {
  const { t } = useTranslation();

  // Tenant-specific Open Graph / Twitter cards
  useEffect(() => {
    if (!restaurant?.name) return;
    const title = restaurant.name;
    const description = (
      restaurant.description
      || restaurant.tagline
      || t("publicMenu.ogDescriptionFallback", "Peça online — cardápio digital")
    ).trim();
    const image = theme?.logo_url
      ? (theme.logo_url.startsWith("http") ? theme.logo_url : `${BACKEND_BASE}${theme.logo_url}`)
      : "";
    const pageUrl = window.location.href;
    document.title = title;
    const upsertMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", value);
    };
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", pageUrl);
    if (image) upsertMeta("property", "og:image", image);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    if (image) upsertMeta("name", "twitter:image", image);
  }, [restaurant?.id, restaurant?.name, restaurant?.description, restaurant?.tagline, theme?.logo_url, t]); // eslint-disable-line

  // WhatsApp cart restore: pre-fill cart from a Carol-generated cart snapshot token
  useEffect(() => {
    if (!cartToken || loading || !restaurant?.id) return;
    fetch(`${API}/public/carts/${cartToken}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.items?.length) return;
        const mapped = data.items.map(it => ({
          id: it.product_id || it.id || "",
          product_id: it.product_id || it.id || "",
          name: it.product_name || it.name || "",
          product_name: it.product_name || it.name || "",
          price: it.unit_price ?? it.price ?? 0,
          unit_price: it.unit_price ?? it.price ?? 0,
          quantity: it.quantity || 1,
          notes: it.notes || "",
          modifiers: it.modifiers || [],
          selected_variations: it.selected_variations || [],
        }));
        setCart(mapped);
        track("cart_restore", { metadata: { source: "whatsapp_token", item_count: mapped.length } });
      })
      .catch(() => {});
  }, [cartToken, loading, restaurant?.id]); // eslint-disable-line

  useEffect(() => {
    if (loading || !restaurant?.id || !searchParams) return;
    const wantsReservation = searchParams.get("reservation") === "1" || searchParams.get("reserve") === "1";
    if (!wantsReservation) return;
    setShowReservation?.(true);
    setReservationConfirmed?.(false);
  }, [loading, restaurant?.id, searchParams]); // eslint-disable-line

  // Reorder: if customer portal stored a pending cart in sessionStorage, restore it once
  useEffect(() => {
    if (loading || !restaurant?.id) return;
    const key = `kaiso_reorder_${restaurantId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return;
    try {
      const items = JSON.parse(stored);
      if (!Array.isArray(items) || items.length === 0) return;
      const mapped = items.map(it => ({
        id: it.product_id || it.id || "",
        cartId: it.product_id || it.id || "",
        product_id: it.product_id || it.id || "",
        name: it.product_name || it.name || "",
        product_name: it.product_name || it.name || "",
        price: it.unit_price ?? it.price ?? 0,
        unit_price: it.unit_price ?? it.price ?? 0,
        quantity: it.quantity || 1,
        notes: it.notes || "",
        modifiers: it.modifiers || [],
        selected_variations: it.selected_variations || [],
      }));
      setCart(mapped);
      sessionStorage.removeItem(key);
      const n = mapped.length;
      toast.success(`${n} produto${n !== 1 ? "s" : ""} adicionado${n !== 1 ? "s" : ""} ao carrinho`);
    } catch {
      sessionStorage.removeItem(key);
    }
  }, [loading, restaurant?.id]); // eslint-disable-line

  // Deep link: redirect if product slug not found after menu loads
  useEffect(() => {
    if (!deepLinkSlug || loading || products.length === 0) return;
    const found = findProductBySlug(products, deepLinkSlug) || findComboBySlug(combos, deepLinkSlug);
    if (!found) {
      toast.info(t("publicMenu.productNotFound", "Produto não encontrado. A carregar o menu..."));
      navigate(`/m/${restaurantId}`, { replace: true });
    }
  }, [deepLinkSlug, loading, products, combos]); // eslint-disable-line

  // Init unified tracking once restaurant config is available
  useEffect(() => {
    if (!restaurant?.id) return;
    initTracking({ restaurantId: restaurant.id, integrations: { ...restaurant.integrations, currency: restaurant.currency || "EUR" }, apiBase: BACKEND_BASE });
    initBehavioralCommerce({ restaurantId: restaurant.id, apiBase: BACKEND_BASE, currency: restaurant.currency || "EUR" });
    trackViewMenu({ source: isTableOrder ? "qr" : "web" });
    if (isTableOrder) trackTableQrOpen({ metadata: { table: tableNumber } });
  }, [restaurant?.id]); // eslint-disable-line

  usePurchaseTracking({ order: checkout.orderConfirmed, restaurant, pixelReady, API });
}
